import { Spinner, SpinnerSize, useTheme } from '@fluentui/react';
import { forwardRef, memo, useMemo } from 'react';
import { useLocation } from 'react-router';
import { TransitionGroup } from 'react-transition-group';
import { SettingsCard } from './SettingsCard';
import { CSSTransitionWithRefRenderProps } from '../../_components/CSSTransitionWithRefRenderProps';
import { AdminRoute } from '../../Router/components/AdminRoute';
import { AccessDenied } from '../../_pages/AccessDenied';

export type TSettingsCategory = {
    title: string;
    shortDescription: string;
    description?: React.ReactNode | React.FC<any>;
    iconName?: string;
    icon?: React.FunctionComponent<
        React.SVGProps<SVGSVGElement> & {
            title?: string;
        }
    >;
    link: string;
    children: TSettingsCategory[] | React.ReactNode;
    /**
     * @default true
     */
    adminOnly?: boolean;
};

type TDashBoardCategory = {
    category: TSettingsCategory;
    loading: boolean;
};

export const Category = memo(({ category: { children, adminOnly = true }, loading }: TDashBoardCategory) => {
    const { pathname } = useLocation();
    const { render, grid } = useMemo(() => {
        const currentItem = children;
        if (Array.isArray(currentItem)) {
            if (currentItem.length === 0) {
                return {
                    render: <AccessDenied />,
                    grid: false,
                };
            }
            return {
                render: (currentItem as TSettingsCategory[]).map((item, i) => {
                    return (
                        <CSSTransitionWithRefRenderProps
                            //
                            key={item.link}
                            mountOnEnter
                            unmountOnExit
                            timeout={{ enter: i * 100, exit: 0 }}
                            classNames={'card'}
                        >
                            {ref => <SettingsCard ref={ref} {...item} />}
                        </CSSTransitionWithRefRenderProps>
                    );
                }),
                grid: true,
            };
        }
        return {
            render: loading ? (
                <Spinner size={SpinnerSize.large} label="Loading settings.." />
            ) : (
                <CSSTransitionWithRefRenderProps
                    //
                    mountOnEnter
                    unmountOnExit
                    timeout={{ enter: 100, exit: 0 }}
                    classNames={'form'}
                >
                    {ref => <FormContainer ref={ref}>{adminOnly ? <AdminRoute>{currentItem}</AdminRoute> : currentItem}</FormContainer>}
                </CSSTransitionWithRefRenderProps>
            ),
            grid: false,
        };
    }, [adminOnly, children, loading]);

    return (
        <TransitionGroup key={pathname} className={grid ? 'fadein-transition-container grid' : 'fadein-transition-container'} appear>
            {render}
        </TransitionGroup>
    );
});

const FormContainer = forwardRef<HTMLDivElement, { children?: React.ReactNode }>(({ children }, ref) => {
    const theme = useTheme();
    return (
        <div style={{ height: '100%', width: '100%', backgroundColor: theme.semanticColors.bodyBackground }} ref={ref}>
            {children}
        </div>
    );
});
